import React, { useState, useEffect, useMemo } from "react";
import useHttp from "../../../hooks/use-http";
import { Spin, Select } from "antd";
import moment from "moment";

import DynamicPieChart from "../../pieChart";
import DynamicColumnChart from "../../columnChart";
import DynamicBarChart from "../../barChart";
import DynamicLineChart from "../../lineChart";

const { Option } = Select;

const TrainingDetail = ({ isMaximized, openMaximize }) => {
  const [loading, setLoading] = useState(true);

  const [totalTrainings, setTotalTrainings] = useState(0);
  const [trainingsByDate, setTrainingsByDate] = useState([]);
  const [trainingsByUser, setTrainingsByUser] = useState([]);

  const [viewType, setViewType] = useState("date");
  const [chartType, setChartType] = useState("pie");

  const [fromDate, setFromDate] = useState("all");
  const [toDate, setToDate] = useState("all");

  const [selectedUser, setSelectedUser] = useState("all");

  const { sendRequest: fetchData } = useHttp();

  const loadData = (opts = {}) => {
    setLoading(true);
    const orgId = localStorage.getItem("orgId") || "";
    const params = new URLSearchParams();
    if (orgId) params.append("orgId", orgId);
    if (opts.userId && opts.userId !== "all")
      params.append("userId", opts.userId);
    if (opts.fromDate) params.append("fromDate", opts.fromDate);
    if (opts.toDate) params.append("toDate", opts.toDate);

    const url = `/api/trainingDetailAgriPromoter/?${params.toString()}`;

    fetchData({ url }, (res) => {
      setTotalTrainings(res?.totalTrainings || 0);
      setTrainingsByDate(res?.trainingsByDate || []);
      setTrainingsByUser(res?.trainingsByUser || []);
      setLoading(false);
    });
  };

  useEffect(() => {
    loadData();
  }, [fetchData]);

  useEffect(() => {
    const opts = {};
    if (selectedUser && selectedUser !== "all") opts.userId = selectedUser;
    if (fromDate && fromDate !== "all") opts.fromDate = fromDate;
    if (toDate && toDate !== "all") opts.toDate = toDate;
    loadData(opts);
  }, [fromDate, toDate, selectedUser]);

  const dateOptions = useMemo(() => {
    const arr = (trainingsByDate || []).map((d) => d.date).filter(Boolean);
    return Array.from(new Set(arr)).sort();
  }, [trainingsByDate]);

  const filteredByDate = useMemo(() => {
    if (!trainingsByDate.length) return [];

    if (fromDate === "all" && toDate === "all") return trainingsByDate;

    const defaultStart = trainingsByDate[0]?.date;
    const defaultEnd = trainingsByDate[trainingsByDate.length - 1]?.date;

    const start = fromDate === "all" ? defaultStart : fromDate;
    const end = toDate === "all" ? defaultEnd : toDate;

    const rangeStart = start <= end ? start : end;
    const rangeEnd = end >= start ? end : start;

    return trainingsByDate.filter(
      (row) => row.date >= rangeStart && row.date <= rangeEnd
    );
  }, [trainingsByDate, fromDate, toDate]);

  const filteredByUser = useMemo(() => {
    if (!trainingsByUser.length) return [];
    if (selectedUser === "all") return trainingsByUser;
    return trainingsByUser.filter(
      (u) => String(u.userId) === String(selectedUser)
    );
  }, [trainingsByUser, selectedUser]);

  let chartDataset = [];
  let displayValue = totalTrainings;

  if (viewType === "date") {
    chartDataset = filteredByDate;
    displayValue = filteredByDate.reduce(
      (sum, r) => sum + (r.trainingCount || 0),
      0
    );
  } else {
    chartDataset = filteredByUser;
    displayValue = filteredByUser.reduce(
      (sum, r) => sum + (r.trainingCount || 0),
      0
    );
  }

  const noData = !loading && !trainingsByDate.length && !trainingsByUser.length;

  if (isMaximized) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          padding: 20,
          background: "#f8fafb",
          borderRadius: 12,
          boxSizing: "border-box",
        }}
      >
        <div
          className="flex items-center mb-4"
          style={{ gap: 10, flexWrap: "wrap" }}
        >
          <Select
            value={viewType}
            style={{ width: 140 }}
            onChange={(v) => {
              setViewType(v);
              setFromDate("all");
              setToDate("all");
              setSelectedUser("all");
            }}
          >
            <Option value="date">Date Wise</Option>
            <Option value="user">User Wise</Option>
          </Select>

          {viewType === "date" && (
            <>
              <Select
                value={fromDate}
                style={{ width: 130, marginLeft: 5, marginRight: 5 }}
                onChange={(v) => setFromDate(v)}
              >
                <Option value="all">From: All</Option>
                {dateOptions.map((d) => (
                  <Option key={d} value={d}>
                    {moment(d).format("DD/MM/YYYY")}
                  </Option>
                ))}
              </Select>

              <Select
                value={toDate}
                style={{ width: 130, marginRight: 5 }}
                onChange={(v) => setToDate(v)}
              >
                <Option value="all">To: All</Option>
                {dateOptions.map((d) => (
                  <Option key={d} value={d}>
                    {moment(d).format("DD/MM/YYYY")}
                  </Option>
                ))}
              </Select>
            </>
          )}

          {viewType === "user" && (
            <Select
              value={selectedUser}
              style={{ width: 220, marginLeft: 5, marginRight: 5 }}
              onChange={(v) => setSelectedUser(v)}
            >
              <Option value="all">All Users</Option>
              {trainingsByUser.map((u) => (
                <Option key={u.userId} value={u.userId}>
                  {u.userName || `User ${u.userId}`}
                </Option>
              ))}
            </Select>
          )}

          <Select
            value={chartType}
            style={{ width: 160, marginLeft: "auto" }}
            onChange={(v) => setChartType(v)}
          >
            <Option value="pie">Pie Chart</Option>
            <Option value="column">Column Chart</Option>
            <Option value="bar">Bar Chart</Option>
            <Option value="line">Line Chart</Option>
          </Select>
        </div>

        {loading ? (
          <div
            style={{
              height: "calc(100% - 80px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Spin />
          </div>
        ) : noData ? (
          <div style={{ padding: 24, textAlign: "center", color: "#64748b" }}>
            <h3 style={{ marginBottom: 4 }}>No Training Data</h3>
            <p style={{ margin: 0 }}>Try changing filters or date range.</p>
          </div>
        ) : (
          <>
            <div
              style={{
                textAlign: "center",
                marginBottom: 16,
                padding: 12,
                background: "#eef2ff",
                borderRadius: 10,
              }}
            >
              <h1
                style={{
                  fontSize: 34,
                  fontWeight: 700,
                  color: "#0f172a",
                  marginBottom: 4,
                }}
              >
                {Number(displayValue || 0).toLocaleString()}
              </h1>
              <p style={{ fontSize: 16, color: "#0f172a", margin: 0 }}>
                {viewType === "date"
                  ? "Total Trainings (Filtered by Date)"
                  : selectedUser === "all"
                  ? "Total Trainings (All Users)"
                  : "Total Trainings (Selected User)"}
              </p>
            </div>

            <div style={{ height: "100%", overflow: "hidden" }}>
              {viewType === "date" && (
                <>
                  {chartType === "pie" && (
                    <DynamicPieChart
                      data={chartDataset}
                      angleField="trainingCount"
                      colorField="date"
                      isMaximized={true}
                    />
                  )}

                  {chartType === "column" && (
                    <DynamicColumnChart
                      data={chartDataset}
                      xField="date"
                      yField="trainingCount"
                      seriesField="date"
                      xAxisTitle="Date"
                      yAxisTitle="Training Count"
                    />
                  )}

                  {chartType === "bar" && (
                    <DynamicBarChart
                      data={chartDataset}
                      xField="date"
                      yField="trainingCount"
                      seriesField="date"
                      xAxisTitle="Date"
                      yAxisTitle="Training Count"
                    />
                  )}

                  {chartType === "line" && (
                    <DynamicLineChart
                      data={chartDataset}
                      xField="date"
                      yField="trainingCount"
                      seriesField="date"
                      xAxisTitle="Date"
                      yAxisTitle="Training Count"
                    />
                  )}
                </>
              )}

              {viewType === "user" && (
                <>
                  {chartType === "pie" && (
                    <DynamicPieChart
                      data={chartDataset}
                      angleField="trainingCount"
                      colorField="userName"
                      isMaximized={true}
                    />
                  )}

                  {chartType === "column" && (
                    <DynamicColumnChart
                      data={chartDataset}
                      xField="userName"
                      yField="trainingCount"
                      seriesField="userName"
                      xAxisTitle="User"
                      yAxisTitle="Training Count"
                    />
                  )}

                  {chartType === "bar" && (
                    <DynamicBarChart
                      data={chartDataset}
                      xField="userName"
                      yField="trainingCount"
                      seriesField="userName"
                      xAxisTitle="User"
                      yAxisTitle="Training Count"
                    />
                  )}

                  {chartType === "line" && (
                    <DynamicLineChart
                      data={chartDataset}
                      xField="userName"
                      yField="trainingCount"
                      seriesField="userName"
                      xAxisTitle="User"
                      yAxisTitle="Training Count"
                    />
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl shadow-md"
      style={{
        background: "#f3e8d3",
        height: 200,
        width: "100%",
        padding: 18,
        position: "relative",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {loading ? (
        <div className="flex justify-center items-center h-full">
          <Spin />
        </div>
      ) : noData ? (
        <div
          className="flex justify-center items-center h-full"
          style={{ color: "#3d5c26" }}
        >
          <div style={{ textAlign: "center" }}>
            <h3 style={{ margin: 0 }}>No Trainings</h3>
            <p style={{ marginTop: 4, fontSize: 13 }}>
              No training records found
            </p>
          </div>
        </div>
      ) : (
        <>
          <div style={{ textAlign: "center" }}>
            <h1 style={{ fontWeight: 700, color: "#3d5c26", marginBottom: 4 }}>
              {Number(totalTrainings || 0).toLocaleString()}
            </h1>
            <h3
              style={{
                fontWeight: 700,
                margin: 0,
                color: "#3d5c26",
              }}
            >
              Total Trainings
            </h3>
          </div>

          <div
            style={{
              position: "absolute",
              bottom: 8,
              right: 18,
              cursor: "pointer",
              fontWeight: 600,
              color: "#3d5c26",
              fontSize: 14,
            }}
            onClick={openMaximize}
          >
            More...
          </div>
        </>
      )}
    </div>
  );
};

export default TrainingDetail;
